import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePeople } from "@/hooks/usePeople";
import { filterPeople, type Person } from "@/lib/people";

/**
 * The shared staff picker (Phase 2, HDQ-TASKN-5). Always yields a LOGIN id
 * (profiles.user_id) — never a profile primary key — and only offers people who have a
 * login. Test accounts are offered (admins test with them) but labelled.
 */
export interface PersonPickerProps {
  value: string | null;
  onChange: (userId: string, person: Person) => void;
  /** Never offer these login ids (e.g. yourself, the current assignee). */
  exclude?: readonly (string | null | undefined)[];
  /** When given, offer ONLY these login ids. */
  only?: readonly string[] | null;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  /**
   * When given, the list starts with a "nobody" option with this label (e.g. "Unassigned")
   * and choosing it calls `onClear`. Omit both for a required pick.
   */
  clearLabel?: string;
  onClear?: () => void;
  /** Extra classes for the trigger (e.g. a compact `h-8 text-xs` in a side panel). */
  triggerClassName?: string;
}

/** Select needs a non-empty value for the "nobody" item; a login id is a uuid, so this can't collide. */
export const PERSON_PICKER_CLEAR_VALUE = "__nobody__";

export function PersonPicker({
  value,
  onChange,
  exclude,
  only,
  placeholder = "Select a person",
  disabled,
  id,
  "aria-label": ariaLabel,
  clearLabel,
  onClear,
  triggerClassName,
}: PersonPickerProps) {
  const clearable = clearLabel !== undefined && onClear !== undefined;
  const { people, byId, isLoading, isError } = usePeople();
  const options = useMemo(() => filterPeople(people, { exclude, only }), [people, exclude, only]);

  const emptyMessage = isLoading
    ? "Loading people…"
    : isError
      ? "Could not load people. Try again."
      : "No one available to choose";

  return (
    <Select
      value={value ?? (clearable ? PERSON_PICKER_CLEAR_VALUE : "")}
      onValueChange={(userId) => {
        if (userId === PERSON_PICKER_CLEAR_VALUE) {
          onClear?.();
          return;
        }
        const person = byId.get(userId);
        if (person) onChange(userId, person);
      }}
      disabled={disabled}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={triggerClassName}>
        <SelectValue placeholder={isLoading ? "Loading people…" : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {clearable && <SelectItem value={PERSON_PICKER_CLEAR_VALUE}>{clearLabel}</SelectItem>}
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          options.map((p) => (
            <SelectItem key={p.userId} value={p.userId}>
              {p.name}
              {p.isTestAccount ? " (test account)" : ""}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
